import { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Badge, Button, Group, PasswordInput, Stack, Tabs, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconLogout } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { Logo } from '../../components/Logo';
import { platformApi, usePlatformAuthStore, type PlatformUser } from './platformApi';

// The Wheels staff control panel lives under /platform with its own
// login — completely separate from any tenant session.

export function PlatformLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = usePlatformAuthStore((s) => s.setSession);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: async () =>
      (
        await platformApi.post<{
          accessToken: string;
          user: PlatformUser;
        }>('/api/platform/auth/login', { email, password })
      ).data,
    onSuccess: (response) => {
      setSession(response.accessToken, response.user);
      navigate('/platform', { replace: true });
    },
    onError: () => {
      notifications.show({ color: 'red', title: t('platform.login.invalid'), message: '' });
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--wheels-bg)]">
      <Stack w={380} gap="lg" p="xl" className="bg-white rounded-lg border border-[var(--wheels-border)]">
        <Group justify="center">
          <Logo />
        </Group>
        <div>
          <Title order={3}>{t('platform.login.title')}</Title>
          <Text size="sm" c="dimmed">
            {t('platform.login.subtitle')}
          </Text>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <Stack gap="sm">
            <TextInput
              label={t('platform.login.email')}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              dir="ltr"
              required
            />
            <PasswordInput
              label={t('platform.login.password')}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
            />
            <Button type="submit" color="wheels" loading={mutation.isPending} fullWidth>
              {t('platform.login.submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </div>
  );
}

export function PlatformLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = usePlatformAuthStore((s) => s.user);
  const clear = usePlatformAuthStore((s) => s.clear);

  if (!user) {
    return <Navigate to="/platform/login" replace />;
  }

  const tab = location.pathname.split('/')[2] ?? 'tenants';

  return (
    <div className="min-h-screen bg-[var(--wheels-bg)]">
      <header className="bg-white border-b border-[var(--wheels-border)] px-6 py-3">
        <Group justify="space-between">
          <Group gap="sm">
            <Logo />
            <Badge variant="light" color="wheels">
              {t('platform.badge')}
            </Badge>
          </Group>
          <Group gap="sm">
            <div className="text-end">
              <Text size="sm" fw={600}>
                {user.fullNameAr}
              </Text>
              <Text size="xs" c="dimmed">
                {user.role === 1 ? t('platform.roles.super') : t('platform.roles.sub')}
              </Text>
            </div>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label={t('platform.logout')}
              onClick={() => {
                clear();
                navigate('/platform/login', { replace: true });
              }}
            >
              <IconLogout size={18} stroke={1.6} />
            </ActionIcon>
          </Group>
        </Group>
      </header>

      <div className="px-6 pt-4">
        <Tabs
          value={tab}
          onChange={(v) => navigate(`/platform/${v === 'tenants' ? '' : v}`)}
          keepMounted={false}
        >
          <Tabs.List>
            <Tabs.Tab value="tenants">{t('platform.tabs.tenants')}</Tabs.Tab>
            <Tabs.Tab value="crm">{t('platform.tabs.crm')}</Tabs.Tab>
            <Tabs.Tab value="reports">{t('platform.tabs.reports')}</Tabs.Tab>
            {user.role === 1 && <Tabs.Tab value="users">{t('platform.tabs.users')}</Tabs.Tab>}
          </Tabs.List>
        </Tabs>
      </div>

      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
}
